'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import Image from 'next/image';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
} from 'lucide-react';

import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { Dialog } from '@/components/ui/dialog';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

/* Localized UI copy for this section. Customer descriptions are localized
 * separately via t.logos[...]. English is the source of truth. */
const LOGOS_COPY = {
  en: {
    eyebrow: 'Our Customers',
    heading: "Used every day by the world's best companies",
    readCaseStudy: 'Read case study',
    readStory: 'Read the story',
    chose: 'Chose us over',
    ofSupportAutomated: 'of support automated',
    close: 'Close',
    prevStory: 'Previous customer story',
    nextStory: 'Next customer story',
    videoStoryAria: 'Customer story video available',
    caseStudyAria: 'Customer story available',
    videoStoryTitle: 'Video story available',
    caseStudyTitle: 'Case study available',
    customerStory: (name: string) => `${name} customer story`,
    learnHow: (name: string) =>
      `Learn how ${name} uses Open to automate their support.`,
  },
  ar: {
    eyebrow: 'عملاؤنا',
    heading: 'أكبر الشركات بالعالم تستخدم Open كل يوم',
    readCaseStudy: 'اقرأ قصة النجاح',
    readStory: 'اقرأ القصة',
    chose: 'اختارونا بدل',
    ofSupportAutomated: 'من الدعم مؤتمت',
    close: 'إغلاق',
    prevStory: 'القصة السابقة',
    nextStory: 'القصة التالية',
    videoStoryAria: 'فيديو لقصة العميل متاح',
    caseStudyAria: 'قصة نجاح متاحة',
    videoStoryTitle: 'فيديو القصة متاح',
    caseStudyTitle: 'قصة النجاح متاحة',
    customerStory: (name: string) => `قصة ${name}`,
    learnHow: (name: string) =>
      `اعرف كيف تستخدم ${name} منصة Open في أتمتة الدعم.`,
  },
};

type LogosCopy = (typeof LOGOS_COPY)['en'];

const useLogosCopy = (): LogosCopy => {
  const { locale } = useTranslation();
  return locale === 'ar' ? LOGOS_COPY.ar : LOGOS_COPY.en;
};

// Background video paths for customers that have story footage available.
const STORY_VIDEOS: Record<string, { videoSrc: string; freezeAt?: number }> = {
  TicketSwap: { videoSrc: '/home/stories/ticketswap.mp4', freezeAt: 2.26 },
  Qoyod: { videoSrc: '/home/stories/qoyod-v2.mp4', freezeAt: 2.14 },
  OTO: { videoSrc: '/home/stories/oto-v2.mp4', freezeAt: 2.26 },
  MoneyGram: { videoSrc: '/home/features/moneygram-shot.mp4' },
  Mollie: { videoSrc: '/home/features/mollie-building.mp4' },
  Tebi: { videoSrc: '/home/features/tebi.mp4' },
  'One Car Now': { videoSrc: '/home/features/ocn.mp4' },
  Trunkrs: { videoSrc: '/home/features/trunkrs.mp4' },
};

// Soft brand-adjacent gradients used when no story video is available.
// Cycled by index so neighbouring cards don't look identical.
const FALLBACK_GRADIENTS = [
  'linear-gradient(135deg, #44322b 0%, #1c1614 70%)',
  'linear-gradient(135deg, #2c2a3a 0%, #14131b 70%)',
  'linear-gradient(135deg, #3a3127 0%, #1b1612 70%)',
  'linear-gradient(135deg, #28333b 0%, #11161b 70%)',
];

// Ordered so the most recognizable / premium brands land in the first row(s) —
// the eye scans top-left first, then across and down (F/Z pattern). On mobile
// (2 columns) this also surfaces the strongest names first.
/* The logo wall is market-specific. The English site shows the original
 * roster; the Arabic site leads with the MENA clients (Snoonu, Wafeq,
 * Syarah, Qoyod, Rewaa, OTO, Toters). Both are signed clients cleared for
 * logo use — do not merge the two lists. */
/** A single logo-wall entry. Declared explicitly (rather than inferred) so the
 * English and Arabic rosters share one type — inference gives each array its
 * own union of optional fields, which then refuse to assign to one another. */
interface LogoItem {
  name: string;
  src: string;
  width: number;
  height: number;
  caseStudyUrl: string | null;
  descriptionKey?: string;
  mono?: boolean;
  small?: boolean;
  automationRate?: string;
  choseUsOver?: string;
  compact?: boolean;
}

const ITEMS_EN: LogoItem[] = [
  {
    name: 'Booking.com',
    src: '/logos/logo-booking.svg',
    width: 130,
    height: 22,
    caseStudyUrl: null,
    descriptionKey: "The world's largest online travel platform",
    mono: true,
    small: true,
  },
  {
    name: 'MoneyGram',
    src: '/logos/logo-moneygram.svg',
    width: 140,
    height: 30,
    caseStudyUrl: '/customer-stories/moneygram',
    descriptionKey:
      'Serving over 50 million people in 200+ countries and territories each year',
    automationRate: '70%',
  },
  {
    name: 'Toters',
    src: '/logos/logo-toters.webp',
    width: 81,
    height: 28,
    caseStudyUrl: null,
    mono: true,
    small: true,
  },
  {
    name: 'Mollie',
    src: '/logos/logo-mollie.svg',
    width: 110,
    height: 24,
    caseStudyUrl: '/customer-stories/mollie',
    descriptionKey: '$6.5 billion payment processor',
    automationRate: '60%',
    small: true,
  },
  {
    name: 'Viva.com',
    src: '/logos/logo-viva.svg',
    width: 100,
    height: 24,
    caseStudyUrl: null,
    descriptionKey: 'The First Tech Bank in Europe for Businesses',
  },
  {
    name: 'TicketSwap',
    src: '/logos/logo-ticketswap.svg',
    width: 145,
    height: 30,
    caseStudyUrl: '/customer-stories/ticketswap',
    descriptionKey: '15 million users across 42 countries',
    automationRate: '67%',
    choseUsOver: 'Intercom',
  },
  {
    name: 'FareHarbor',
    src: '/logos/logo-fareharbor.svg',
    width: 140,
    height: 28,
    caseStudyUrl: '/customer-stories/fareharbor',
    descriptionKey: 'A Booking Holdings company with 23,000+ operators',
  },
  {
    name: 'More.com',
    src: '/logos/logo-morecom.svg',
    width: 125,
    height: 26,
    caseStudyUrl: '/customer-stories/more',
    descriptionKey: 'Selling over 34 million tickets',
    automationRate: '67%',
    small: true,
  },
  {
    name: 'Indie Campers',
    src: '/logos/logo-indiecampers.png',
    width: 92,
    height: 32,
    caseStudyUrl: null,
    descriptionKey: "Europe's leading campervan rental marketplace",
    mono: true,
  },
  {
    name: 'Trunkrs',
    src: '/logos/logo-trunkrs.svg',
    width: 125,
    height: 28,
    caseStudyUrl: '/customer-stories/trunkrs',
    descriptionKey: '61,180+ Google reviews',
    automationRate: '80%',
    small: true,
  },
  {
    name: 'Tebi',
    src: '/logos/logo-tebi.svg',
    width: 95,
    height: 24,
    caseStudyUrl: null,
    descriptionKey: 'Tebi raised a €30M Series B in June 2025',
  },
  {
    name: 'Sunday Natural',
    src: '/logos/logo-sunday-wordmark.png',
    width: 150,
    height: 22,
    caseStudyUrl: null,
    descriptionKey: 'Premium natural supplements crafted in Berlin',
    mono: true,
  },
  {
    name: 'One Car Now',
    src: '/logos/logo-ocn.png',
    width: 90,
    height: 28,
    caseStudyUrl: '/customer-stories/ocn',
    descriptionKey: '$86M-funded car subscription platform in Mexico',
    automationRate: '60%',
  },
  {
    name: 'KuantoKusta',
    src: '/logos/logo-kuantokusta.svg',
    width: 145,
    height: 30,
    caseStudyUrl: null,
    descriptionKey: "Portugal's No. 2 Price Comparison Champion",
  },
  {
    name: 'IU Internationale Hochschule',
    src: '/logos/logo-iu.svg',
    width: 136,
    height: 47,
    caseStudyUrl: null,
    descriptionKey: "Germany's largest university with 130,000+ students",
    mono: true,
  },
  {
    name: 'Snel Een Taxi',
    src: '/logos/logo-sneleentaxi.svg',
    width: 80,
    height: 20,
    caseStudyUrl: '/customer-stories/sneleentaxi',
    descriptionKey:
      'Over half a million customers served with nationwide booking ease',
    automationRate: '65%',
    compact: true,
  },
  {
    // Typeset wordmark (SVG text) — muted like the rest via `mono`.
    name: 'OTO',
    src: '/logos/logo-oto.svg',
    width: 120,
    height: 40,
    caseStudyUrl: '/customer-stories/oto',
    descriptionKey: 'Reinventing car ownership across the Gulf',
    automationRate: '77%',
    mono: true,
    small: true,
  },
  {
    name: 'Snoonu',
    // Official Snoonu wordmark (from snoonu.com). Fresh filename to bypass the
    // optimizer cache that was still serving the old typeset placeholder.
    src: '/logos/logo-snoonu-official.svg',
    width: 163,
    height: 34,
    caseStudyUrl: null,
    descriptionKey: "Qatar's leading super-app for on-demand delivery",
    mono: true,
    small: true,
  },
];

const ITEMS_AR: LogoItem[] = [
  {
    name: 'Booking.com',
    src: '/logos/logo-booking.svg',
    width: 130,
    height: 22,
    caseStudyUrl: null,
    descriptionKey: "The world's largest online travel platform",
    mono: true,
    small: true,
  },
  {
    name: 'MoneyGram',
    src: '/logos/logo-moneygram.svg',
    width: 140,
    height: 30,
    caseStudyUrl: '/customer-stories/moneygram',
    descriptionKey:
      'Serving over 50 million people in 200+ countries and territories each year',
    automationRate: '70%',
  },
  {
    name: 'Mollie',
    src: '/logos/logo-mollie.svg',
    width: 110,
    height: 24,
    caseStudyUrl: '/customer-stories/mollie',
    descriptionKey: '$6.5 billion payment processor',
    automationRate: '60%',
    small: true,
  },
  {
    name: 'Viva.com',
    src: '/logos/logo-viva.svg',
    width: 100,
    height: 24,
    caseStudyUrl: null,
    descriptionKey: 'The First Tech Bank in Europe for Businesses',
  },
  {
    name: 'TicketSwap',
    src: '/logos/logo-ticketswap.svg',
    width: 145,
    height: 30,
    caseStudyUrl: '/customer-stories/ticketswap',
    descriptionKey: '15 million users across 42 countries',
    automationRate: '67%',
    choseUsOver: 'Intercom',
  },
  {
    name: 'More.com',
    src: '/logos/logo-morecom.svg',
    width: 125,
    height: 26,
    caseStudyUrl: '/customer-stories/more',
    descriptionKey: 'Selling over 34 million tickets',
    automationRate: '67%',
    small: true,
  },
  // --- Row 2: MENA / Gulf clients ---------------------------------------
  {
    name: 'Snoonu',
    // Official Snoonu wordmark (from snoonu.com). Fresh filename to bypass the
    // optimizer cache that was still serving the old typeset placeholder.
    src: '/logos/logo-snoonu-official.svg',
    width: 163,
    height: 34,
    caseStudyUrl: null,
    descriptionKey: "Qatar's leading super-app for on-demand delivery",
    mono: true,
    small: true,
  },
  {
    name: 'Wafeq',
    src: '/logos/logo-wafeq.svg',
    width: 127,
    height: 28,
    caseStudyUrl: null,
    automationRate: '73%',
    descriptionKey: 'Leading accounting software in Saudi Arabia',
    mono: true,
    small: true,
  },
  {
    name: 'Syarah',
    src: '/logos/logo-syarah.svg',
    width: 80,
    height: 37,
    caseStudyUrl: '/customer-stories/syarah',
    mono: true,
    small: true,
  },
  {
    name: 'Qoyod',
    src: '/logos/logo-qoyod.png',
    width: 117,
    height: 26,
    caseStudyUrl: '/customer-stories/qoyod',
    descriptionKey: '25,000 businesses trust Qoyod for accounting',
    mono: true,
    small: true,
  },
  {
    name: 'Rewaa',
    src: '/logos/logo-rewaa.svg',
    width: 89,
    height: 30,
    caseStudyUrl: null,
    descriptionKey: 'Trusted by thousands of merchants worldwide',
    mono: true,
    small: true,
  },
  {
    // Typeset wordmark (SVG text) — muted like the rest via `mono`.
    name: 'OTO',
    src: '/logos/logo-oto.svg',
    width: 120,
    height: 40,
    caseStudyUrl: '/customer-stories/oto',
    descriptionKey: 'Reinventing car ownership across the Gulf',
    automationRate: '77%',
    mono: true,
    small: true,
  },
  // --- Row 3 ------------------------------------------------------------
  {
    name: 'Toters',
    src: '/logos/logo-toters.webp',
    width: 81,
    height: 28,
    caseStudyUrl: null,
    mono: true,
    small: true,
  },
  {
    name: 'One Car Now',
    src: '/logos/logo-ocn.png',
    width: 90,
    height: 28,
    caseStudyUrl: '/customer-stories/ocn',
    descriptionKey: '$86M-funded car subscription platform in Mexico',
    automationRate: '60%',
  },
  {
    name: 'KuantoKusta',
    src: '/logos/logo-kuantokusta.svg',
    width: 145,
    height: 30,
    caseStudyUrl: null,
    descriptionKey: "Portugal's No. 2 Price Comparison Champion",
  },
  {
    name: 'IU Internationale Hochschule',
    src: '/logos/logo-iu.svg',
    width: 136,
    height: 47,
    caseStudyUrl: null,
    descriptionKey: "Germany's largest university with 130,000+ students",
    mono: true,
  },
  {
    name: 'Snel Een Taxi',
    src: '/logos/logo-sneleentaxi.svg',
    width: 80,
    height: 20,
    caseStudyUrl: '/customer-stories/sneleentaxi',
    descriptionKey:
      'Over half a million customers served with nationwide booking ease',
    automationRate: '65%',
    compact: true,
  },
  {
    name: 'Trunkrs',
    src: '/logos/logo-trunkrs.svg',
    width: 125,
    height: 28,
    caseStudyUrl: '/customer-stories/trunkrs',
    descriptionKey: '61,180+ Google reviews',
    automationRate: '80%',
    small: true,
  },
];

const LogoCard = ({ logo, onOpen }: { logo: LogoItem; onOpen: () => void }) => {
  const copy = useLogosCopy();
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (hovered) {
      timerRef.current = setTimeout(() => setExpanded(true), 500);
    } else {
      setExpanded(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [hovered]);

  // Three card flavors, in order of richness:
  //   1. Has a story video → opens the modal carousel (button).
  //   2. No video but has a case study URL → links straight to that page.
  //   3. Neither → render as a non-interactive logo (no empty modal,
  //      no dead link). KuantoKusta-style logos with just a tagline
  //      fall into this bucket and used to open an empty black modal.
  const hasStoryVideo = Boolean(STORY_VIDEOS[logo.name]);
  const hasCaseStudy = Boolean(logo.caseStudyUrl);

  const hoverAffordance = (
    <>
      {/* Hover expand affordance – plus that grows into "Read case study".
          Only shown when there's actually somewhere to click. */}
      {(hasStoryVideo || hasCaseStudy) && (
        <motion.span
          aria-hidden="true"
          layout
          transition={{
            type: 'spring',
            stiffness: 320,
            damping: 30,
            mass: 0.7,
          }}
          className={cn(
            'absolute right-3 top-3 z-10 flex h-7 items-center justify-center overflow-hidden rounded-full border border-neutral-200 bg-white text-neutral-700',
            'opacity-0 transition-opacity duration-200 ease-out',
            'group-hover:opacity-100',
            expanded ? 'gap-1.5 pl-2 pr-3' : 'w-7',
          )}
        >
          <Maximize2 className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.span
                key="label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="whitespace-nowrap text-[11px] font-medium"
              >
                {copy.readCaseStudy}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.span>
      )}

      {/* "Chose us over X" tagline on hover */}
      {logo.choseUsOver && (
        <motion.span
          aria-hidden="true"
          initial={false}
          animate={{
            opacity: hovered ? 1 : 0,
            y: hovered ? 0 : 4,
          }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={cn(
            'pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2',
            'whitespace-nowrap rounded-full bg-black/85 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-white backdrop-blur-sm',
          )}
        >
          {copy.chose} {logo.choseUsOver}
        </motion.span>
      )}
    </>
  );

  const logoImage = (
    <div className="relative flex min-h-[88px] items-center justify-center p-4 transition-all duration-300 sm:min-h-[140px] sm:p-8">
      <Image
        src={logo.src}
        alt={logo.name}
        width={logo.width}
        height={logo.height}
        className={cn(
          'w-auto max-w-[160px] object-contain opacity-70 transition-all duration-300 group-hover:opacity-100 sm:opacity-60',
          logo.compact ? 'h-14' : 'h-8',
          logo.mono && 'grayscale brightness-0',
        )}
      />
      {/* Customer-story availability indicator. Shown for any logo that
          opens *something* on click — either the video modal or a case
          study page. Hidden on hover because the "+ Read case study"
          pill takes over the same corner. */}
      {(hasStoryVideo || hasCaseStudy) && (
        <span
          aria-label={hasStoryVideo ? copy.videoStoryAria : copy.caseStudyAria}
          title={hasStoryVideo ? copy.videoStoryTitle : copy.caseStudyTitle}
          className="pointer-events-none absolute right-3 top-3 z-10 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 transition-opacity duration-200 group-hover:opacity-0"
        />
      )}
      {hoverAffordance}
    </div>
  );

  if (hasStoryVideo) {
    return (
      <button
        type="button"
        onClick={onOpen}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label={copy.customerStory(logo.name)}
        className="group relative block w-full ltr:text-left rtl:text-right"
      >
        {logoImage}
      </button>
    );
  }

  if (hasCaseStudy) {
    return (
      <Link
        href={logo.caseStudyUrl!}
        prefetch={true}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label={copy.customerStory(logo.name)}
        className="group relative block w-full ltr:text-left rtl:text-right"
      >
        {logoImage}
      </Link>
    );
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={logo.name}
      className="group relative block w-full"
    >
      {logoImage}
    </div>
  );
};

const CustomerStoryModalContent = ({
  logo,
  description,
  story,
  gradient,
  allLogos,
  onSelect,
}: {
  logo: LogoItem;
  description: string;
  story?: { videoSrc: string; freezeAt?: number };
  gradient: string;
  allLogos: LogoItem[];
  onSelect: (logo: LogoItem) => void;
}) => {
  const copy = useLogosCopy();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Customers that have story videos – used for prev/next arrow navigation
  const storyLogos = allLogos.filter((l) => STORY_VIDEOS[l.name]);
  const storyIdx = storyLogos.findIndex((l) => l.name === logo.name);

  const step = (delta: number) => {
    if (storyLogos.length === 0) return;
    const baseIdx = storyIdx === -1 ? 0 : storyIdx;
    const nextIdx = (baseIdx + delta + storyLogos.length) % storyLogos.length;
    onSelect(storyLogos[nextIdx]);
  };

  // Seek to a great frame, then start playing.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !story) return;
    const seek = () => {
      if (story.freezeAt !== undefined) {
        try {
          video.currentTime = story.freezeAt;
        } catch {
          // ignore
        }
      }
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    if (video.readyState >= 1) seek();
    else video.addEventListener('loadedmetadata', seek, { once: true });
    return () => video.removeEventListener('loadedmetadata', seek);
  }, [story]);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        )}
      />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'flex w-[min(92vw,460px)] flex-col gap-3 text-white',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          'duration-300 ease-out',
        )}
      >
        {/* Accessibility: Radix expects a Title/Description for screen readers */}
        <DialogPrimitive.Title className="sr-only">
          {copy.customerStory(logo.name)}
        </DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">
          {description || copy.learnHow(logo.name)}
        </DialogPrimitive.Description>

        <div className="relative w-full">
          <div
            className="radius-18 relative aspect-[4/5] w-full overflow-hidden rounded-[2rem] bg-neutral-950 shadow-2xl [clip-path:inset(0_round_18px)] [contain:paint] [transform:translateZ(0)]"
            style={!story ? { background: gradient } : undefined}
          >
            <AnimatePresence mode="sync" initial={false}>
              <motion.div
                key={logo.name}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
                style={!story ? { background: gradient } : undefined}
              >
                {story && (
                  <video
                    ref={videoRef}
                    src={
                      story.freezeAt !== undefined
                        ? `${story.videoSrc}#t=${story.freezeAt}`
                        : story.videoSrc
                    }
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}

                {/* Dark gradient for legibility – heavier at the bottom so the text reads */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.12) 22%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.88) 78%, rgba(0,0,0,0.96) 100%)',
                  }}
                />

                {/* Bottom content */}
                <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col gap-1 p-7">
                  {logo.automationRate ? (
                    <>
                      <div className="font-sans text-5xl font-medium text-white sm:text-6xl">
                        {logo.automationRate}
                      </div>
                      <div className="mt-1 text-sm text-white/70">
                        {copy.ofSupportAutomated}
                      </div>
                    </>
                  ) : (
                    <div className="font-sans text-2xl font-medium text-white sm:text-3xl">
                      {logo.name}
                    </div>
                  )}
                  {description && (
                    <p className="mt-2 max-w-[90%] text-[13px] leading-snug text-white/65">
                      {description}
                    </p>
                  )}
                  {logo.caseStudyUrl && (
                    <Link
                      href={logo.caseStudyUrl}
                      prefetch={true}
                      className="group/cta mt-5 inline-flex items-center gap-1.5 self-start text-sm font-medium text-white transition-colors hover:text-white/85"
                    >
                      {copy.readStory}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-0.5" />
                    </Link>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Close – stays static across story transitions */}
            <DialogPrimitive.Close
              className={cn(
                'absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full',
                'bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
              )}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{copy.close}</span>
            </DialogPrimitive.Close>
          </div>

          {/* Prev / Next navigation – floating OUTSIDE the video card, centered vertically */}
          {storyLogos.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label={copy.prevStory}
                className={cn(
                  'absolute top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full',
                  'bg-white/15 text-white backdrop-blur-md transition-colors duration-200',
                  'hover:bg-white hover:text-neutral-900',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                  'left-3 md:-left-14 md:bg-white/20',
                )}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label={copy.nextStory}
                className={cn(
                  'absolute top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full',
                  'bg-white/15 text-white backdrop-blur-md transition-colors duration-200',
                  'hover:bg-white hover:text-neutral-900',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                  'right-3 md:-right-14 md:bg-white/20',
                )}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
};

interface LogosProps {
  variant?: 'marquee' | 'grid' | 'cards';
  backgroundColor?: 'sand' | 'white' | 'transparent';
  /** Hide the small "Our Customers" eyebrow above the heading. */
  hideEyebrow?: boolean;
}

export default function Logos({
  variant = 'marquee',
  backgroundColor = 'white',
  hideEyebrow = false,
}: LogosProps) {
  const { t, locale } = useTranslation();
  const copy = useLogosCopy();
  const ITEMS = locale === 'ar' ? ITEMS_AR : ITEMS_EN;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const activeLogo = activeIndex !== null ? ITEMS[activeIndex] : null;
  const activeStory = activeLogo ? STORY_VIDEOS[activeLogo.name] : undefined;
  const activeGradient =
    activeIndex !== null
      ? FALLBACK_GRADIENTS[activeIndex % FALLBACK_GRADIENTS.length]
      : FALLBACK_GRADIENTS[0];
  const activeDescription = useMemo(() => {
    if (!activeLogo || !activeLogo.descriptionKey) return '';
    return (
      (t.logos as Record<string, string>)[activeLogo.descriptionKey] ||
      activeLogo.descriptionKey
    );
  }, [activeLogo, t.logos]);

  const descriptionFor = (logo: LogoItem) => {
    if (!logo.descriptionKey) return '';
    return (
      (t.logos as Record<string, string>)[logo.descriptionKey] ||
      logo.descriptionKey
    );
  };

  const bgClass =
    backgroundColor === 'white'
      ? 'bg-white'
      : backgroundColor === 'transparent'
        ? 'bg-transparent'
        : 'bg-[#fafaf9]';

  return (
    <>
      <Dialog
        open={activeLogo !== null}
        onOpenChange={(open) => {
          if (!open) setActiveIndex(null);
        }}
      >
        {activeLogo && (
          <CustomerStoryModalContent
            logo={activeLogo}
            description={activeDescription}
            story={activeStory}
            gradient={activeGradient}
            allLogos={ITEMS}
            onSelect={(logo) => {
              const i = ITEMS.findIndex((it) => it.name === logo.name);
              if (i !== -1) setActiveIndex(i);
            }}
          />
        )}
      </Dialog>

      <section className={`relative overflow-hidden py-12 md:py-24 ${bgClass}`}>
        <div className="relative z-10 container">
          {(variant === 'grid' || variant === 'cards') && (
            <SubtleEntrance className="mb-16 text-center">
              {!hideEyebrow && (
                <p className="mb-3 text-xs font-medium tracking-widest text-black/40 uppercase">
                  {copy.eyebrow}
                </p>
              )}
              <h2 className="font-serif text-xl text-black/80 md:text-2xl">
                {copy.heading}
              </h2>
            </SubtleEntrance>
          )}
          {variant === 'cards' ? (
            // Flex-wrap + justify-center: 2 / 4 / 6 cards per row. The widths
            // subtract the exact gap total so full rows sit flush edge-to-edge.
            // 18 logos → three even rows of 6 on desktop.
            <div className="flex flex-wrap justify-center gap-3">
              {ITEMS.map((logo, index) => {
                const itemCls =
                  'group block h-24 w-[calc((100%_-_0.75rem)/2)] sm:w-[calc((100%_-_2.25rem)/4)] lg:w-[calc((100%_-_3.75rem)/6)]';
                const hasCaseStudy = Boolean(logo.caseStudyUrl);
                const card = (
                  <span className="radius-18 relative flex h-full w-full items-center justify-center overflow-hidden bg-[#efece7] px-4 transition-colors group-hover:bg-[#e8e4dd]">
                    <Image
                      src={logo.src}
                      alt={logo.name}
                      width={logo.width}
                      height={logo.height}
                      loading={index < 6 ? 'eager' : 'lazy'}
                      className={cn(
                        'w-auto max-w-[112px] object-contain opacity-70 transition-all duration-300 ease-out',
                        logo.small ? 'h-[18px]' : logo.compact ? 'h-8' : 'h-5',
                        logo.mono && 'grayscale brightness-0',
                        hasCaseStudy
                          ? 'group-hover:-translate-y-1 group-hover:opacity-0'
                          : 'group-hover:opacity-100',
                      )}
                    />
                    {hasCaseStudy && (
                      <span className="pointer-events-none absolute inset-0 flex translate-y-1 items-center justify-center gap-1.5 text-sm font-medium text-neutral-900 opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100">
                        {copy.readCaseStudy}
                        <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
                      </span>
                    )}
                  </span>
                );
                return logo.caseStudyUrl ? (
                  <Link
                    key={`cards-${index}`}
                    href={logo.caseStudyUrl}
                    prefetch={true}
                    aria-label={copy.customerStory(logo.name)}
                    className={itemCls}
                  >
                    {card}
                  </Link>
                ) : (
                  <div
                    key={`cards-${index}`}
                    aria-label={logo.name}
                    className={itemCls}
                  >
                    {card}
                  </div>
                );
              })}
            </div>
          ) : variant === 'marquee' ? (
            <div className="relative mt-10">
              <div className="flex w-full">
                {/* First marquee group */}
                <div className="animate-marquee flex shrink-0 items-center gap-16">
                  {ITEMS.map((logo, index) => (
                    <div key={`marquee-1-${index}`}>
                      <Link
                        href={logo.caseStudyUrl || '/customer-stories'}
                        aria-label={copy.customerStory(logo.name)}
                        className="block px-4 py-6"
                      >
                        <Image
                          src={logo.src}
                          alt={logo.name}
                          width={logo.width}
                          height={logo.height}
                          loading={index < 6 ? 'eager' : 'lazy'}
                          className={cn(
                            'w-auto max-w-[140px] object-contain opacity-40 transition-opacity duration-300 hover:opacity-70',
                            logo.compact ? 'h-12' : 'h-7',
                            logo.mono && 'grayscale brightness-0',
                          )}
                        />
                      </Link>
                    </div>
                  ))}
                </div>
                {/* Second marquee group */}
                <div className="animate-marquee flex shrink-0 items-center gap-16">
                  {ITEMS.map((logo, index) => (
                    <div key={`marquee-2-${index}`}>
                      <Link
                        href={logo.caseStudyUrl || '/customer-stories'}
                        aria-label={copy.customerStory(logo.name)}
                        aria-hidden={true}
                        tabIndex={-1}
                        className="block px-4 py-6"
                      >
                        <Image
                          src={logo.src}
                          alt={logo.name}
                          width={logo.width}
                          height={logo.height}
                          className={cn(
                            'w-auto max-w-[140px] object-contain opacity-40 transition-opacity duration-300 hover:opacity-70',
                            logo.compact ? 'h-12' : 'h-7',
                            logo.mono && 'grayscale brightness-0',
                          )}
                        />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {ITEMS.map((logo, index) => {
                const rowIndex = Math.floor(index / 4);
                return (
                  <SubtleEntrance key={`grid-${index}`} delay={rowIndex * 0.1}>
                    <LogoCard
                      logo={logo}
                      onOpen={() => setActiveIndex(index)}
                    />
                  </SubtleEntrance>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
